// Kiid "Explore flavors".
//
// 1. Variants mode (the element has data-option): clicking a flavor tile selects that flavor variant.
//
//    a. If the page has the theme's own variant picker (radio buttons / selects named
//       options[<Option name>]-... with data-single-option-selector and a form attribute equal to the
//       product form id), the tile drives that picker, so the theme switches the variant, price and gallery
//       exactly as if the shopper had clicked the picker.
//
//    b. If the page has NO variant picker (for example a page built around the Product Selector block),
//       the tile does what a picker would do on its own, so apps that follow the selected variant keep
//       working:
//         - sets ?variant=ID in the address bar (history.replaceState, no reload),
//         - sets the product form's hidden input[name="id"] and fires a change event on it,
//         - fires "rubik-variant-id-changed" on document (the hook the Rubik Variant Images app listens to,
//           which also covers returning to the variant the page first loaded on),
//         - fires "kiid:variant:change" on document for anything else that wants it.
//       The Rubik app also polls the ?variant= address every 200ms, so the URL alone is enough for it to
//       swap the images.
//
//    The tile is always a real link (?variant=ID), so without JavaScript it simply loads that variant.
//
// 2. Scroll arrows (phones, when the row scrolls): the previous / next buttons scroll the row sideways and
//    hide themselves at either end.

if (!customElements.get('kiid-flavor-explorer')) {
  customElements.define(
    'kiid-flavor-explorer',
    class KiidFlavorExplorer extends HTMLElement {
      connectedCallback() {
        this.tiles = [...this.querySelectorAll('.kfe__tile')];
        this.setupArrows();

        this.optionName = this.dataset.option;
        if (!this.optionName) return; // collection mode: plain links

        this.formId = this.dataset.form;
        this.variantTiles = [...this.querySelectorAll('[data-option-value]')];
        // The flavor shown as current on first render, used when there is no picker to read it from.
        this.directValue = this.variantTiles.find((tile) => tile.classList.contains('kfe__tile--current'))?.dataset.optionValue ?? null;

        this.addEventListener('click', (event) => {
          const tile = event.target.closest('[data-option-value]');
          if (!tile || event.metaKey || event.ctrlKey || event.shiftKey) return;
          if (this.select(tile)) event.preventDefault();
        });

        this.onChange = () => this.sync();
        document.addEventListener('change', this.onChange);
        this.sync();
      }

      disconnectedCallback() {
        if (this.onChange) document.removeEventListener('change', this.onChange);
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      // ---- scroll arrows ----

      setupArrows() {
        this.list = this.querySelector('.kfe__list');
        this.prev = this.querySelector('.kfe__arrow--prev');
        this.next = this.querySelector('.kfe__arrow--next');
        if (!this.list || !this.prev || !this.next) return;

        const step = (direction) => () =>
          this.list.scrollBy({ left: direction * this.list.clientWidth * 0.75, behavior: 'smooth' });
        this.prev.addEventListener('click', step(-1));
        this.next.addEventListener('click', step(1));

        this.list.addEventListener('scroll', () => this.updateArrows(), { passive: true });
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.updateArrows());
          this.resizeObserver.observe(this.list);
        }
        window.addEventListener('load', () => this.updateArrows());
        this.revealCurrent();
        this.updateArrows();
      }

      updateArrows() {
        if (!this.list || !this.prev || !this.next) return;
        const max = this.list.scrollWidth - this.list.clientWidth;
        this.prev.hidden = this.list.scrollLeft <= 2;
        this.next.hidden = max <= 2 || this.list.scrollLeft >= max - 2;
      }

      // If the flavor being viewed is off-screen in the scrolling row, bring it into view (sideways only,
      // so the page itself doesn't jump).
      revealCurrent() {
        if (!this.list) return;
        const current = this.list.querySelector('.kfe__tile--current');
        if (!current) return;
        const item = current.closest('.kfe__item') || current;
        const target = item.offsetLeft - (this.list.clientWidth - item.offsetWidth) / 2;
        this.list.scrollLeft = Math.max(0, target);
      }

      // ---- variants mode ----

      inputs() {
        const prefix = `options[${this.optionName}]`;
        return [...document.querySelectorAll('[data-single-option-selector]')].filter(
          (input) =>
            (input.getAttribute('name') || '').startsWith(prefix) &&
            (!this.formId || input.getAttribute('form') === this.formId)
        );
      }

      // Returns true when the click was handled (picker driven or variant set directly), false when the
      // plain link should be followed.
      select(tile) {
        const value = tile.dataset.optionValue;
        const inputs = this.inputs();

        const radio = inputs.find((input) => input.type === 'radio' && input.value === value);
        if (radio) {
          radio.click();
          return true;
        }
        const select = inputs.find((input) => input.tagName === 'SELECT');
        if (select) {
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }

        // No picker on the page: behave like one would.
        return this.selectDirectly(tile);
      }

      selectDirectly(tile) {
        const variantId = tile.dataset.variantId;
        if (!variantId) return false;

        // 1. The address bar. Apps and theme code that follow the selected variant read ?variant=.
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variantId);
          window.history.replaceState(window.history.state, '', url.toString());
        } catch (error) {
          return false;
        }

        // 2. The product form's hidden variant id, as the theme's own picker code updates it.
        const idInput = this.idInput();
        if (idInput) {
          idInput.value = variantId;
          idInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // 3. Events: the Rubik Variant Images app's hook, and our own.
        const detail = { variantId: Number(variantId), option: this.optionName, value: tile.dataset.optionValue };
        document.dispatchEvent(new CustomEvent('rubik-variant-id-changed', { detail: { variantId: detail.variantId } }));
        document.dispatchEvent(new CustomEvent('kiid:variant:change', { detail }));

        this.directValue = tile.dataset.optionValue;
        this.sync();
        return true;
      }

      idInput() {
        const selector = 'input[name="id"]';
        if (this.formId) {
          const own = [...document.querySelectorAll(selector)].find((input) => input.getAttribute('form') === this.formId);
          if (own) return own;
        }
        return this.closest('[data-section-type]')?.querySelector(selector) ?? null;
      }

      currentValue() {
        const inputs = this.inputs();
        const radio = inputs.find((input) => input.type === 'radio' && input.checked);
        if (radio) return radio.value;
        const select = inputs.find((input) => input.tagName === 'SELECT');
        if (select) return select.value;
        return this.directValue; // no picker on the page
      }

      sync() {
        const current = this.currentValue();
        if (current === null || current === undefined) return;
        this.variantTiles.forEach((tile) => {
          const isCurrent = tile.dataset.optionValue === current;
          tile.classList.toggle('kfe__tile--current', isCurrent);
          if (isCurrent) tile.setAttribute('aria-current', 'true');
          else tile.removeAttribute('aria-current');
        });
      }
    }
  );
}
